// One server answer, six stores. Both the SSR seed (<StoreSeed>) and the poll
// (<StoreSync>) hand every row to every store; each ignores the kinds it does
// not own. Kept here rather than inside StoreSync because the seed needs
// exactly the same fan-out, and two copies of this list would drift the moment
// a seventh store appears.
//
// ─── AND DELETIONS, WHICH USED TO HAVE NO WAY IN ────────────────────────────
//
// Until the poll became a diff, a row deleted on the server simply stopped
// arriving, and every store only upserts, so every other open tab kept it until
// a reload: Delete week's dates, a grade reset. Now a deletion arrives as a
// key, and each store removes it. There are two sources:
//
//   - the diff's `deleted` list: keys removed since the tab's cursor;
//   - a FULL dump that replaces what the tab holds (`replace`): the server could
//     not say what changed — the tab's cursor was counted on another file — so
//     every key the server sent this tab before and does not send now is gone.
//
// The second needs to know which keys came from the server, and that is what
// `held` is: every key a server answer has carried and none has since deleted.
// A key this tab wrote and the server has not yet echoed is NOT in it, so a
// replace can never drop a row that exists only here — and every store still
// asks the pending-write guard before it removes anything.
//
// ─── AND A DUMP CARRIES SOME GROUPS, NOT THE STORE ─────────────────────────
//
// A tab holds only the groups its pages read (lib/store-groups.ts,
// lib/route-store-groups.ts), and every whole dump it is handed — the seed, a
// group load, a full answer — is a dump of SOME groups. So a replace drops only
// held keys of the kinds those groups' dumps carry: another group's keys are
// not in the dump because nobody asked for them, and reading their absence as a
// deletion would empty every page the tab is not on. The same rule, one group
// wide, as the one for `grade`: no dump carries a grade (a diff-only kind), so
// no replace ever drops one — otherwise every resync would delete every grade
// the tab holds, the one the editor just fetched included.
//
// What a dump of `scripts` DOES say about grades is "this tab may have missed
// a change": a replace means another epoch, and a heal or a first load moves
// the cursor past whatever grade writes happened in between. So every dump of
// `scripts` tells the script store to forget which grades it is current on
// (forgetGradeKnowledge): the held ones stay on screen, and the editor asks
// again for the ones it shows.
//
// And a dump of a group is what makes the tab HOLD it (lib/store-cursor.ts):
// a page that reads the group renders from then on, and the script store's
// `hydrated` — the editor's "has the server answered about scripts?" — is the
// `scripts` group's. A diff never marks a group held: it says nothing about a
// group the tab has not loaded, and a 304 says nothing at all.

import { applyServerRows as applyScript, forgetGradeKnowledge, markHydrated } from "@/lib/ironside-script-store";
import { applyServerRows as applyFeedback } from "@/lib/ironside-feedback-store";
import { applyServerRows as applyGrader } from "@/lib/ironside-grader-feedback-store";
import { applyServerRows as applyRubricComments } from "@/lib/ironside-rubric-comment-store";
import { applyServerRows as applyRunComments } from "@/lib/ironside-run-comment-store";
import { applyServerRows as applySchedule } from "@/lib/schedule-store";
import { STORE_GROUPS, dumpKindsOf, type StoreGroup } from "@/lib/store-groups";
import { markGroupsHeld } from "@/lib/store-cursor";

export interface StoreRow { kind: string; id: string; data: unknown }
/** A key without its row: what a deletion is. */
export interface StoreKey { kind: string; id: string }

// A kind never holds a newline, so this cannot collide.
const keyOf = (k: StoreKey): string => `${k.kind}\n${k.id}`;
const held = new Map<string, StoreKey>();

/** `true` is every group (the whole store), `false` none. */
const groupsOf = (g: boolean | readonly StoreGroup[]): readonly StoreGroup[] => (g === true ? STORE_GROUPS : g === false ? [] : g);

/**
 * Apply one server answer to every store.
 *
 * `deleted`: keys the server no longer has. `replace`: the groups `rows` is the
 * WHOLE of, so anything of theirs held and not in it is deleted too — pass it
 * ONLY for a full dump the server sent in place of a diff (StoreSync), or for
 * the server's seed, never for a partial answer: a replace with `[]` deletes
 * every held key of those groups. `dumped`: the groups `rows` carries a whole
 * dump of, whether or not it replaces (a replace always is one): the tab now
 * holds them, and a dump of `scripts` leaves the grades held unvouched for (the
 * header). `true` means every group, `false` none.
 */
export function applyAllStores(
  rows: StoreRow[],
  deleted: StoreKey[] = [],
  replace: boolean | readonly StoreGroup[] = false,
  dumped: boolean | readonly StoreGroup[] = replace,
): void {
  let gone = deleted;
  const replacing = new Set(dumpKindsOf(groupsOf(replace)));
  if (replacing.size > 0) {
    const present = new Set(rows.map(keyOf));
    gone = [...deleted];
    for (const [k, key] of held) if (!present.has(k) && replacing.has(key.kind)) gone.push(key);
  }
  for (const r of rows) held.set(keyOf(r), { kind: r.kind, id: r.id });
  for (const d of gone) held.delete(keyOf(d));

  applyScript(rows, gone);
  applyFeedback(rows, gone);
  applyGrader(rows, gone);
  applyRubricComments(rows, gone);
  applyRunComments(rows, gone);
  applySchedule(rows, gone);
  const landed = groupsOf(dumped);
  if (landed.includes("scripts")) {
    forgetGradeKnowledge();
    // The editor waits on this before deciding a script doesn't exist
    // (ironside-script-store §hydrated).
    markHydrated();
  }
  markGroupsHeld(landed);
}
